<template>
  <a-spin size="large" :spinning="loading">
    <div class="account-manager" v-if="linkedinAccounts.length > 0">
      <!-- Header -->
      <div class="header-section">
        <h1 class="page-title">{{ $t("LinkedInAccountManager") }}</h1>
        <p class="page-subtitle">
          {{ $t("ManageDailyLimits") }}
          <span style="color: red">{{ $t("LTDAccountTips") }}</span>
        </p>
      </div>

      <!-- Account Selector -->
      <div class="account-selector">
        <span class="selector-label">{{ $t("SelectLinkedInAccount") }}:</span>
        <a-select
          v-model="selectedAccount"
          class="account-select"
          @change="handleAccountChange"
        >
          <a-select-option
            v-for="(account, index) in linkedinAccounts"
            :key="index"
            :value="index"
          >
            <span class="account-option">
              🔗
              <span class="account-email">{{ account.linkedin_email }}</span>
              <span class="campaign-badge"
                >&nbsp;{{ account.running_campaign_count }}&nbsp;{{
                  $t("Campaigns")
                }}</span
              >
            </span>
          </a-select-option>
        </a-select>
      </div>

      <!-- Account Settings -->
      <div class="settings-card">
        <div class="settings-header">
          <div class="settings-title">
            <a-icon type="setting" class="settings-icon" />
            <span>{{ $t("AccountSettings") }}</span>
          </div>

          <a-button type="primary" class="save-btn" @click="updateHandler">
            <a-icon type="save" /> {{ $t("SaveChanges") }}
          </a-button>
        </div>

        <!-- Daily Limit -->
        <div class="daily-limit-section">
          <div class="limit-row">
            <span class="limit-label"
              >{{ $t("DailyConnectionRequestLimit") }}:</span
            >
            <div class="limit-control">
              <limit-box
                :value="dailyLimit"
                :min="1"
                :max="100000"
                @change="handleLimitChange"
              />
              <span class="limit-unit">{{ $t("requestsPerDay") }}</span>
            </div>
            <div class="limit-stats">
              <div class="sent-today">
                <span class="stat-item sent">
                  <a-icon
                    type="check-circle"
                    theme="filled"
                    class="stat-icon"
                  />
                  {{ $t("SentToday") }}:
                  <strong
                    >{{ linkedin_info.today_sent }}/{{
                      linkedin_info.request_limit
                    }}</strong
                  >
                </span>
                <a-progress
                  :percent="
                    (linkedin_info.today_sent / linkedin_info.request_limit) *
                    100
                  "
                  :show-info="false"
                  :stroke-color="'#12B981'"
                  class="limit-progress"
                />
              </div>
              <div class="stat-item remaining">
                📅
                {{ $t("Remaining") }}:
                <strong style="color: #12b981">{{
                  dailyLimit - linkedin_info.today_sent > 0
                    ? dailyLimit - linkedin_info.today_sent
                    : 0
                }}</strong>
              </div>
            </div>
          </div>
        </div>

        <!-- Allocation Mode -->
        <div class="allocation-section" v-if="!linkedinAccounts[this.selectedAccount].is_ltd">
          <h3 class="section-title">{{ $t("AllocationMode") }}:</h3>
          <allocation-options
            :allocationMode="allocationMode"
            :daily-limit="dailyLimit"
            :active-campaigns-count="campaignQueue.length"
            @change="handleAllocationChange"
          />
          <p class="switchTips">
            {{ $t("SwitchModeTips") }}
          </p>
        </div>
        <!-- Campaign Execution Queue -->
        <div class="queue-section" v-if="!linkedinAccounts[this.selectedAccount].is_ltd">
          <div class="queue-header">
            <div class="queue-title">
              <a-icon type="ordered-list" />
              <span>{{ $t("CampaignExecutionQueue") }}</span>
            </div>
            <span class="drag-hint">
              <a-icon type="drag" /> {{ $t("DragToReorder") }}
            </span>
          </div>

          <div class="queue-table-header">
            <span class="col-queue" style="text-align: right">{{
              $t("Queue")
            }}</span>
            <span class="col-name">{{ $t("CampaignName") }}</span>
            <span class="col-agent">{{ $t("Agent") }}</span>
            <span class="col-active">{{ $t("Active") }}</span>
            <span class="col-plan">{{ $t("RemainingToday") }}</span>
            <span class="col-dayLimit">{{ $t("UsedToday") }}</span>
          </div>

          <draggable
            v-model="campaignQueue"
            class="queue-list"
            @end="handleDragEnd"
          >
            <div
              v-for="(campaign, index) in campaignQueue"
              :key="campaign.id"
              class="queue-item"
            >
              <div class="col-queue">
                <a-icon type="drag" class="drag-handle" />
                <span class="queue-number">{{ index + 1 }}</span>
              </div>
              <div class="col-name">
                <div class="campaign-info">
                  <span class="campaign-name">{{
                    campaign.campaign_name
                  }}</span>
                  <div class="campaign-meta">
                    <span>{{ $t("Created") }}: {{ campaign.create_time }}</span>
                    <span
                      v-if="
                        campaign.today_send_count &&
                        campaign.campaign_friend_request_count <=
                          campaign.today_send_count &&
                        linkedin_info.allocation_mode == 1
                      "
                      class="remaining-badge"
                    >
                      <a-icon type="team" />{{
                        $t("OnlyLeftToComplete", {
                          count: campaign.campaign_friend_request_count,
                        })
                      }}
                    </span>
                    <span
                      v-if="
                        campaign.inherit_from_queue &&
                        linkedin_info.allocation_mode == 1
                      "
                      class="surplus-badge"
                    >
                      <a-icon type="arrow-up" /> +{{
                        campaign.today_send_count
                      }}
                      {{ $t("fromCampaign") }}{{ campaign.inherit_from_queue }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-agent">
                <span
                  class="agent-badge"
                  :class="campaign.campaign_type == 1 ? 'sales' : 'recruiter'"
                >
                  <!-- <a-icon type="robot" /> -->
                  <span v-if="campaign.campaign_type == 1">💼</span>
                  <span v-else>🎯</span>
                  {{
                    campaign.campaign_type == 1
                      ? $t("AISalesRepLabel")
                      : $t("AIRecruiterLabel")
                  }}
                </span>
              </div>
              <div class="col-active">
                <a-switch
                  v-model="campaign.status"
                  @change="(val) => toggleCampaign(campaign, val)"
                />
              </div>
              <div class="col-plan" v-if="linkedin_info.allocation_mode == 1">
                <span class="plan-needed" v-if="campaign.send_status == 3">
                  {{ $t("Done") }}
                </span>
                <span
                  class="plan-needed"
                  v-else-if="
                    index == 0 &&
                    campaign.send_status != 3 &&
                    campaign.today_send_count &&
                    (campaign.today_send_count >= linkedin_info.request_limit ||
                      campaign.today_send_count <= linkedin_info.request_limit)
                  "
                >
                  <strong>{{ campaign.today_send_count }}</strong>
                </span>
                <span
                  class="plan-needed"
                  v-else-if="
                    !campaign.inherit_from_queue && campaign.today_send_count
                  "
                >
                  <strong>{{ campaign.today_send_count }}</strong>
                </span>
                <span
                  class="plan-today"
                  v-else-if="campaign.inherit_from_queue"
                >
                  <strong>{{ campaign.today_send_count }}</strong>
                  {{ $t("today") }}
                  <span v-if="campaign.inherit_from_queue" class="surplus-from"
                    >{{ $t("SurplusFrom") }} #{{
                      campaign.inherit_from_queue
                    }}</span
                  >
                </span>

                <span
                  v-else-if="
                    campaign.send_status == 2 || campaign.send_status == 4
                  "
                  class="plan-waiting"
                >
                  <strong>—</strong><br />{{
                    campaign.send_status == 2
                      ? $t("WaitingInQueue")
                      : $t("PendingCrawl")
                  }}
                </span>
              </div>
              <div class="col-plan" v-else>
                <span class="plan-needed" v-if="campaign.send_status == 3">
                  {{ $t("Done") }}
                </span>
                <span
                  v-else-if="
                    campaign.send_status == 2 || campaign.send_status == 4
                  "
                  class="plan-waiting"
                >
                  <strong>—</strong><br />{{
                    campaign.send_status == 2
                      ? $t("WaitingInQueue")
                      : $t("PendingCrawl")
                  }}
                </span>
                <span class="plan-needed" v-else>
                  <strong>{{ campaign.today_send_count }}</strong>
                </span>
              </div>
              <div class="col-dayLimit">
                {{ campaign.today_completed_count }}
              </div>
            </div>
          </draggable>

          <!-- Sequential Execution Notice -->
          <div
            v-if="linkedin_info.allocation_mode === 1"
            class="sequential-notice"
          >
            <a-icon
              type="exclamation-circle"
              theme="filled"
              class="notice-icon"
            />
            <div>
              <div class="notice-header">
                <span class="notice-title">{{
                  $t("SequentialExecutionNotice")
                }}</span>
              </div>
              <p class="notice-content">
                {{ $t("SequentialNoticeContent1")
                }}<strong>{{ $t("Queue") }} #1</strong
                >{{ $t("SequentialNoticeContent2")
                }}<strong>{{ $t("SequentialNoticeContent3") }}</strong
                >{{ $t("SequentialNoticeContent4") }}
              </p>
              <div class="notice-tip">
                💡
                <span>{{ $t("SequentialNoticeTip") }}</span>
              </div>
            </div>
          </div>
          <div
            v-if="linkedin_info.allocation_mode === 2"
            class="sequential-notice"
          >
            <a-icon
              type="exclamation-circle"
              theme="filled"
              class="notice-icon"
            />
            <div>
              <div class="notice-header">
                <span class="notice-title">
                  {{ $t("EqualExecutionNotice") }}</span
                >
              </div>
              <p class="notice-content">
                {{ $t("EqualNoticeContent") }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="nodata" v-else><a-empty /></div>
    <CampaignModalCon
      :showModal.sync="showModal"
      v-if="showModal"
      @saveLinkedIn="saveLinkedIn"
      :newLinkedIn="newLinkedIn"
    />
  </a-spin>
</template>
<script>
import draggable from "vuedraggable";
import LimitBox from "@/components/limitBox";
import AllocationOptions from "@/components/allocationOptions";

import CampaignModalCon from "@/layouts/globalModals/CampaignModalCon";

export default {
  components: {
    draggable,
    LimitBox,
    AllocationOptions,
    CampaignModalCon,
  },
  data() {
    return {
      loading: false,
      linkedin_info: {},
      selectedAccount: 0,
      dailyLimit: 25,
      realLimit: 1,
      sentToday: 18,
      allocationMode: 1,
      modalVisible: false,
      equalModalVisible: false,
      savedCampaign: null,
      linkedinAccounts: [],
      showModal: false,
      //选中的link
      selectedLinkObj: {},
      newLinkedIn: [],
      // linkedinAccounts: [
      //   { id: 1, email: "kobeyang@jhpasia.cn", campaignCount: 3 },
      //   { id: 2, email: "user2@example.com", campaignCount: 2 },
      // ],
      campaignQueue: [],
    };
  },
  computed: {
    activeCampaigns() {
      return this.campaignQueue.filter((c) => c.active);
    },
  },
  watch: {
    lang() {
      this.getData();
    },
  },
  i18n: require("../i18n"),
  created() {
    this.getLinkList();
  },
  methods: {
    saveLinkedIn() {
      this.showModal = false;
    },
    updateHandler() {
      this.loading = true;
      this.$axios(
        "/linkedin/update_linkedin_limits",
        {
          linkedin_id: this.linkedinAccounts[this.selectedAccount].linkedin_id,
          request_limit: this.dailyLimit,
          allocation_mode: this.allocationMode,
        },
        "post",
      ).then((res) => {
        // this.loading = false;
        if (res.code == 200) {
          this.linkedin_info.request_limit = this.dailyLimit;
          this.getDetail(
            this.linkedinAccounts[this.selectedAccount].linkedin_id,
          );
        }
      });
    },
    getDetail(id) {
      this.loading = true;
      this.$axios(
        "/sales/campaign_execution_detail",
        { ads_id: id },
        "post",
      ).then((res) => {
        this.loading = false;
        if (res.code == 200) {
          // this.selectedLinkObj = res.data;
          const { linkedin_info, campaign_list } = res.data;

          this.linkedin_info = linkedin_info || {};
          this.allocationMode = parseInt(linkedin_info.allocation_mode);
          this.campaignQueue = campaign_list
            ? campaign_list.map((item) => {
                item.status = item.status == 1 ? true : false;
                return item;
              })
            : [];
          this.dailyLimit = linkedin_info.request_limit || 1;
          this.realLimit = linkedin_info.request_limit || 1;
        }
      });
    },
    getLinkList() {
      this.loading = true;
      this.$axios("/linkedin/list", { campaign_type: null }, "post").then(
        (res) => {
          if (res.code == 200) {
            
            let arr = []
            res.data.forEach(item=>{
              if(!item.is_ltd){
                arr.push(item)
              }
            })
            this.linkedinAccounts = arr
            let { id } = this.$route.query;
            if (id) {
              this.linkedinAccounts.forEach((item, index) => {
                if (item.linkedin_id == id) {
                  this.selectedAccount = index;
                  this.selectedLinkObj = item;
                }
              });
              this.getDetail(id);
            } else {
              this.selectedLinkObj = this.linkedinAccounts[0];
              if(this.selectedLinkObj){
                this.getDetail(this.selectedLinkObj.linkedin_id);
              }else{
                this.loading = false
              }
              
            }
          } else {
            this.loading = false;
          }
        },
      );
    },
    getData() {
      // API call to fetch account data
      this.loading = true;
      // this.$axios("/linkedin/account_manager", {}, "post").then((res) => {
      //   if (res.code == 200) {
      //     this.linkedinAccounts = res.data.accounts;
      //     this.campaignQueue = res.data.campaigns;
      //     this.dailyLimit = res.data.dailyLimit;
      //     this.sentToday = res.data.sentToday;
      //   }
      //   this.loading = false;
      // });
      setTimeout(() => {
        this.loading = false;
      }, 500);
    },
    handleAccountChange(index) {
      // Load account specific data
      this.selectedLinkObj = this.linkedinAccounts[index];
      this.getDetail(this.selectedLinkObj.linkedin_id);
    },
    handleLimitChange(newValue) {
      this.dailyLimit = newValue;
    },
    handleAllocationChange(mode) {
      this.allocationMode = parseInt(mode);
    },

    handleModalClose() {
      this.modalVisible = false;
    },
    handleEqualModalClose() {
      this.equalModalVisible = false;
    },
    handleManageCampaigns() {
      this.modalVisible = false;
      this.equalModalVisible = false;
      // Navigate to campaign list page
      this.$router.push("/sales/salesReps/campaign");
    },
    handleDragEnd(event) {
      const { oldIndex, newIndex, item } = event;
      console.log("拖拽前索引:", oldIndex);
      console.log("拖拽后索引:", newIndex);

      if (oldIndex !== newIndex) {
        // 方式1：从更新后的数组中获取拖拽后的对象
        const movedItem = this.campaignQueue[newIndex];
        console.log("被拖拽的对象:", movedItem);

        // 方式2：通过 DOM 元素的 key 或 dataset 获取
        const draggedElement = item;
        console.log("拖拽的DOM元素:", draggedElement);
        this.loading = true;
        this.$axios(
          "/sales/campaign_queue_reorder",
          {
            campaign_id: movedItem.campaign_id,
            queue_position: newIndex + 1,
          },
          "post",
        ).then((res) => {
          if (res.code == 200) {
            this.$axios(
              "/sales/campaign_execution_detail",
              { ads_id: this.selectedLinkObj.linkedin_id },
              "post",
            ).then((res) => {
              this.loading = false;
              if (res.code == 200) {
                // this.selectedLinkObj = res.data;
                const { linkedin_info, campaign_list } = res.data;

                this.linkedin_info = linkedin_info || {};
                this.allocationMode = linkedin_info.allocation_mode;
                this.campaignQueue = campaign_list
                  ? campaign_list.map((item) => {
                      item.status = item.status == 1 ? true : false;
                      return item;
                    })
                  : [];
                this.dailyLimit = linkedin_info.request_limit || 1;
              }
            });
          } else {
            this.$message.error(res.msg);
          }
        });
      }
    },
    toggleCampaign(campaign, active) {
      console.log("Campaign toggled:", campaign.id, active);
      let { campaign_id, status, campaign_type } = campaign;

      if (campaign.is_not_friend) {
        this.showModal = true;
        this.newLinkedIn = [this.linkedin_info.linkedin_mail];
        campaign.status = !campaign.status;
        return;
      }
      let url =
        campaign_type == 2
          ? "/recruiter/campaign_change_status"
          : "/sales/campaign_change_status";
      this.$axios(
        url,
        { campaign_id: campaign_id, change_status: status ? 1 : 0 },
        "post",
      ).then((res) => {
        if (res.code == 200) {
          console.log(res.msg);

          //this.dashBorderData = res.data;
        } else {
          campaign.status = !status;
        }
      });
    },
  },
};
</script>
<style scoped lang="less">
.nodata{
  display: flex;
  align-items: center;
  justify-content: center;
}
.switchTips {
  color: red;
  padding-top: 20px;
}
.account-manager {
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;
}

// Header
.header-section {
  margin-bottom: 24px;

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #1f1f1f;
    margin-bottom: 8px;
  }

  .page-subtitle {
    font-size: 14px;
    color: #666;
  }
}

// Account Selector
.account-selector {
  background: #fff;
  padding: 16px 24px;
  border-radius: 8px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;

  .selector-label {
    font-weight: 500;
    color: #333;
  }

  .account-select {
    width: 460px;

    .account-option {
      display: flex;
      align-items: center;
      gap: 8px;

      .linkedin-icon {
        color: #0077b5;
        font-size: 16px;
      }

      .account-email {
        flex: 1;
      }

      .campaign-badge {
        background: #d1fae6;
        color: #079669;
        padding: 0 8px;
        border-radius: 4px;
        font-size: 12px;
      }
    }
  }
}

// Settings Card
.settings-card {
  background: #fff;
  border-radius: 8px;
  margin-bottom: 24px;
  overflow: hidden;
  padding: 16px 24px;
}

.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 20px;

  .settings-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 600;

    .settings-icon {
      color: #666;
    }
  }

  .save-btn {
    background: #12b981;
    // border-color: #0e756a;

    // &:hover {
    //   background: #0a5a52;
    //   border-color: #0a5a52;
    // }
  }
}

// Daily Limit Section
.daily-limit-section {
  padding: 24px;
  background: #effdf3;
  border: 1px solid #bdf9d0;
  border-radius: 10px;
  border-bottom: 1px solid #f0f0f0;
  .limit-row {
    display: flex;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
  }

  .limit-label {
    font-weight: 500;
    color: #333;
  }

  .limit-control {
    display: flex;
    align-items: center;
    gap: 8px;

    .limit-unit {
      color: #666;
      font-size: 14px;
      margin-left: 15px;
    }
  }

  .limit-stats {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-left: auto;
    .sent-today {
      display: flex;
      background-color: #fff;
      padding: 10px 14px;
      border-radius: 8px;
    }
    .stat-item {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;

      &.sent {
        color: #333;
        margin-right: 10px;
        .stat-icon {
          color: #12b981;
        }
      }

      &.remaining {
        color: #666;
        background-color: #fff;
        padding: 10px 14px;
        .stat-icon {
          color: #999;
        }
      }
    }

    .limit-progress {
      width: 120px;
    }
  }
}

// Allocation Section
.allocation-section {
  padding: 24px 0;

  .section-title {
    font-size: 14px;
    font-weight: 500;
    color: #333;
    margin-bottom: 16px;
  }
}

// Queue Section

.queue-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  .queue-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 600;

    i {
      color: #666;
    }
  }

  .drag-hint {
    font-size: 13px;
    color: #999;
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

.queue-table-header {
  display: grid;
  grid-template-columns: 80px 1fr 120px 80px 120px 80px;
  gap: 16px;
  padding: 12px 16px;
  background: #fafafa;
  font-size: 13px;
  font-weight: 500;
  color: #666;
  border-bottom: 1px solid #e6e7eb;
  margin-bottom: 8px;
}

.queue-list {
  .queue-item {
    display: grid;
    grid-template-columns: 80px 1fr 120px 80px 120px 80px;
    gap: 16px;
    padding: 16px;
    border-bottom: 1px solid #e6e7eb;
    align-items: center;
    cursor: move;
    transition: background 0.2s;

    &:hover {
      background: #fafafa;
    }

    &:last-child {
      border-bottom: none;
    }

    .col-queue {
      display: flex;
      align-items: center;
      gap: 8px;
      .drag-handle {
        color: #d9d9d9;
        cursor: move;
      }

      .queue-number {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: #f0f0f0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 600;
        color: #666;
        margin-left: 20px;
        &.top {
          background: #12b981;
          color: #fff;
        }
        &.second {
          background: #f59e0c;
          color: #fff;
        }
        &.third {
          background: #9da3af;
          color: #fff;
        }
      }
    }

    .col-name {
      .campaign-info {
        .campaign-name {
          font-weight: 500;
          color: #1f1f1f;
          display: block;
          margin-bottom: 4px;
        }

        .campaign-meta {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 12px;
          color: #999;

          .remaining-badge {
            color: #0e756a;
            display: flex;
            align-items: center;
            gap: 4px;
          }

          .surplus-badge {
            color: #faad14;
            display: flex;
            align-items: center;
            gap: 4px;
          }
        }
      }
    }

    .col-agent {
      .agent-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        border-radius: 100px;
        font-size: 12px;
        font-weight: bold;

        &.sales {
          background: #dbeafe;
          color: #1e4ed8;
        }

        &.recruiter {
          background: #fee7f4;
          color: #c0185d;
        }

        i {
          font-size: 12px;
        }
      }
    }

    .col-active {
      text-align: left;
    }

    .col-plan {
      .plan-needed {
        color: #707683;
        strong {
          font-size: 18px;
          color: #12b981;
        }
      }

      .plan-today {
        color: #707683;
        strong {
          font-size: 18px;
          color: #fa8c16;
        }

        .surplus-from {
          display: block;
          font-size: 11px;
          color: #faad14;
          margin-top: 2px;
        }
      }

      .plan-waiting {
        color: #999;
        font-size: 13px;
        text-align: center;
      }
    }
    .col-dayLimit {
      text-align: center;
    }
  }
}
.col-dayLimit {
  text-align: center;
}

// Sequential Execution Notice
.sequential-notice {
  margin-top: 16px;
  background: linear-gradient(to right, #fef3c2, #fee790);
  border: 1px solid #f9b853;
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  .notice-icon {
    color: #faad14;
    font-size: 18px;
    margin-right: 10px;
    margin-top: 3px;
  }
  .notice-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;

    .notice-title {
      font-weight: 600;
      font-size: 14px;
      color: #92400d;
    }
  }

  .notice-content {
    font-size: 13px;
    color: #78350f;
    line-height: 1.6;
    margin-bottom: 12px;

    strong {
      // color: #1f1f1f;
    }
  }

  .notice-tip {
    background: #fff;
    border-radius: 6px;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #92400d;

    i {
      color: #faad14;
    }
  }
}
::v-deep .ant-select-selection--single {
  height: auto;
  padding: 10px 0;
}
</style>
